import React, { useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import BobPhoneInput from '../../components/BobPhoneInput';

import AutoComplete from '../../components/FormFields/Autocomplete';

import CancelButton from '../../components/CancelButton';
import ConfirmButton from '../../components/ConfirmButton';
import ImageUploadInput from '../Global/ImageUploadInput';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './EditTechnician.module.css';

const ValidationSchema = Yup.object().shape({
    firstName: Yup.string()
        .required(),
    lastName: Yup.string()
        .required(),
    email: Yup.string()
        .required(),
    phone: Yup.string()
        .required(),
    title: Yup.string()
        .required(),
    seniorityLevel: Yup.string()
        .required(),
    specialties: Yup.array()
        .required()
});

const EditTechnician = ({
    close,
    clearErrors,
    open,
    title,
    isLoading,
    errors,
    selectedNode,
    toggleLoading,
    closeEditPanel,
    selectedTechnician,
    seniorityLevels,
    specialties,
    ...props
}) => {
    const [imageImport, setImageImport] = useState(null);
    const [phoneCountry, setPhoneCountry] = useState();
    let selectedSpecialties = [];

    if (selectedTechnician.technicianSpecialtyIds) {
        selectedSpecialties = specialties[0].items.filter(({ id }) => selectedTechnician.technicianSpecialtyIds.includes(id));
    }

    const formik = useFormik({
        initialValues: {
            firstName:      selectedTechnician.firstName || '',
            lastName:       selectedTechnician.lastName || '',
            email:          selectedTechnician.email || '',
            phone:          selectedTechnician.phone || '',
            title:          selectedTechnician.title || '',
            seniorityLevel: selectedTechnician.seniorityLevel || '',
            specialties:    selectedSpecialties || [],
            imageImport:    selectedTechnician.imageImport || null
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            if (phoneCountry && phoneCountry.dialCode === values.phone) {
                values.phone = '';
            }

            const {
                updateTechnician
            } = props;

            let isCreated;

            const specialtyIds = values.specialties.map(specialty => specialty.id);

            const body = {
                firstName:              values.firstName,
                lastName:               values.lastName,
                email:                  values.email,
                phone:                  values.phone,
                title:                  values.title,
                seniorityLevel:         values.seniorityLevel,
                technicianSpecialtyIds: specialtyIds,
                imageImport,
                id:                     selectedTechnician.id
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await updateTechnician(body);
                }
                if (isCreated) {
                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        setImageImport(null);
        clearErrors();

        close();
    };

    const handleImageImport = ({image }) => {
        setImageImport(image);
    };

    const handleImageClear = () => {
        setImageImport(null);
    };

    const onClose = () => {
        closeEditPanel();
        setImageImport(null);
    };

    const handlePhoneNoChange = (value) => {
        formik.setFieldValue('phone', value, false);
    };

    return (
        <Paper className={classes.formColumn}>
            <div className={classes.columns}>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <ImageUploadInput
                            fileName={selectedTechnician.name}
                            hasPhoto={selectedTechnician.hasPhoto}
                            photoUrl={selectedTechnician.photoUrl}
                            accept=".png"
                            upload={handleImageImport}
                            clear={handleImageClear}
                            initialText="Add Technician Picture"
                        />
                    </div>
                </div>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/hardHatFlat.svg" alt="Edit Client Icon" />
                        <span>Edit Technician</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            A Technician is representative of a person who performs work on a Client's Site.
                        </p>
                        <p>
                            Select a Title.
                        </p>
                        <p>
                            Select a Seniority Level.
                        </p>
                        <p>
                            Select one or more Specialties.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="firstName"
                                name="firstName"
                                type="text"
                                value={formik.values.firstName}
                                label="First Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.firstName) && Boolean(formik.touched.firstName)}
                                helperText={Boolean(formik.touched.firstName) && formik.errors.firstName}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="lastName"
                                name="lastName"
                                type="text"
                                value={formik.values.lastName}
                                label="Last Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.lastName) && Boolean(formik.touched.lastName)}
                                helperText={Boolean(formik.touched.lastName) && formik.errors.lastName}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="email"
                                name="email"
                                type="text"
                                value={formik.values.email}
                                label="Email"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                                helperText={Boolean(formik.touched.email) && formik.errors.email}
                            />
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapper}>
                                <FormLabel
                                    classes={{
                                        error: classes.phoneLabelError,
                                        root:  classes.phoneLabel
                                    }}
                                    focused={false}
                                >
                                    Phone
                                </FormLabel>
                                <BobPhoneInput
                                    name="phone"
                                    label="Phone Number"
                                    data-cy="user-phone"
                                    value={formik.values.phone}
                                    onChange={
                                        (v, d, e, fv) => { 
                                            setPhoneCountry(d);
                                            handlePhoneNoChange(v);
                                        }
                                    }
                                    error={Boolean(formik.errors.phone) && Boolean(formik.touched.phone)}
                                    helperText={Boolean(formik.touched.phone) && formik.errors.phone}
                                />
                            </FormControl>
                        </div>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="title"
                                name="title"
                                type="text"
                                value={formik.values.title}
                                label="Title"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.title) && Boolean(formik.touched.title)}
                                helperText={Boolean(formik.touched.title) && formik.errors.title}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="seniorityLevel"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.seniorityLevel) && Boolean(formik.touched.seniorityLevel)}
                            >
                                Seniority Level
                            </InputLabel>
                            <Select
                                value={formik.values.seniorityLevel}
                                id="seniorityLevel"
                                onChange={formik.handleChange}
                                name="seniorityLevel"
                                classes={{
                                    root: classes.input
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.seniorityLevel) && Boolean(formik.touched.seniorityLevel)}
                            >
                                {
                                    seniorityLevels.map(seniorityLevel =>
                                        <MenuItem
                                            key={seniorityLevel.id}
                                            value={seniorityLevel.label}
                                        >
                                            {seniorityLevel.label}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.seniorityLevel &&
                            formik.errors.seniorityLevel && <FormHelperText className={classes.error}>{formik.errors.seniorityLevel}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Specialties"
                                name="specialties"
                                id="specialties"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                multiple={true}
                                className={classes.autoComplete}
                                onChange={formik.handleChange}
                                options={specialties}
                                value={formik.values.specialties}
                                error={Boolean(formik.errors.specialties) && Boolean(formik.touched.specialties)}
                                helperText={Boolean(formik.touched.specialties) && formik.errors.specialties}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
            <div className={classes.footer}>
                <CancelButton
                    cancel={onClose}
                />
                <ConfirmButton
                    isLoading={isLoading}
                    label={'Confirm'}
                    onClick={formik.handleSubmit}
                />
            </div>
        </Paper>
    );
};

EditTechnician.defaultProps = {
    businessTypes: [],
    close:         () => {},
    companyInfo:   {},
    contacts:      [],
    errors:        [],
    isLoading:     false,
    title:         ''
};

EditTechnician.propTypes = {
    businessTypes: array,
    close:         func,
    companyInfo:   object,
    contacts:      array,
    errors:        array,
    isLoading:     bool,
    title:         string
};

const mapStateToProps = state => {
    return {
        selectedTechnician: state.architect.selectedTechnician,
        errors:             state.messages.errors,
        isLoading:          state.ui.isLoading,
        seniorityLevels:    state.architect.seniorityLevels,
        specialties:        state.architect.specialties
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditTechnician);
